import { FC, ReactNode } from 'react'
import styles from './stateCard.module.scss'

interface StateCardProps {
  action?: { label: string; onClick: () => void }
  children?: ReactNode
  description: ReactNode
  icon: 'brand' | 'competitor' | 'processing' | 'prompt'
  // Smaller block inside a page section rather than a page-filling card.
  inline?: boolean
  title: string
}

const ICONS: Record<StateCardProps['icon'], ReactNode> = {
  brand: (
    <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
      <path
        d="M3 21h18M5 21V9l5-3v15M10 21V4l9 4v13M13 10h3M13 13h3M13 16h3M7 12h1M7 15h1"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  ),
  competitor: (
    <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
      <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M16 16l4.5 4.5M18.5 3.5v4M16.5 5.5h4"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.5"
      />
    </svg>
  ),
  processing: (
    <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
      <path
        d="M20 12a8 8 0 1 1-2.34-5.66M20 4v4h-4"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  ),
  prompt: (
    <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
      <path
        d="M4 5h16v11H9l-5 4V5zM8 9h8M8 12h5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  ),
}

// Empty and "being analysed" states: a grey icon tile, a title, a line of
// explanation and an optional call to action.
export const StateCard: FC<StateCardProps> = ({
  action,
  children,
  description,
  icon,
  inline = false,
  title,
}) => (
  <div className={inline ? styles.inline : styles.card}>
    <div className={styles.body}>
      <span className={styles.icon}>{ICONS[icon]}</span>
      <h2 className={styles.title}>{title}</h2>
      <p className={styles.description}>{description}</p>
      {action && (
        <button
          className={styles.action}
          onClick={action.onClick}
          type="button"
        >
          {action.label}
        </button>
      )}
      {children}
    </div>
  </div>
)
